import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Coffee, LogOut, Plus, Pencil, Trash2, ArrowRight, Clock, DollarSign, Phone, Store, Truck, Check, PowerOff } from "lucide-react";
import { toast } from "sonner";

const STATUS_FLOW = ["pending", "confirmed", "brewing", "ready", "completed"];
const STATUS_LABEL = { pending: "Pending", confirmed: "Confirmed", brewing: "Brewing", ready: "Ready", completed: "Done", cancelled: "Cancelled" };
const NEXT_STATUS = { confirmed: "brewing", brewing: "ready", ready: "completed" };
const STATUS_COLOR = { pending: "#D69F4C", confirmed: "#7A8B76", brewing: "#C05A46", ready: "#7A8B76", completed: "#1A1514", cancelled: "#7B7573" };

const CATEGORIES = [
  { key: "drinks", label: "Drinks" },
  { key: "sizes", label: "Sizes" },
  { key: "milks", label: "Milks" },
  { key: "syrups", label: "Syrups" },
  { key: "extras", label: "Extras" },
];

function currency(n) { return `$${Number(n || 0).toFixed(2)}`; }

function ConfirmDialog({ order, refresh }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [scheduled, setScheduled] = useState(order.pickup_time || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const where = order.fulfillment_type === "delivery"
        ? `deliver to ${order.delivery_group || order.delivery_address || "your address"}`
        : "pick up at the address I'm sending now";
      const suffix = order.fulfillment_type === "pickup"
        ? " Pickup address: [ADD ADDRESS]. Reply with any changes."
        : " Reply with any changes.";
      setMsg(`Hi ${order.customer_name.split(" ")[0]}! I'll ${where} on ${order.pickup_date} at ${order.pickup_time}.${suffix}`);
      setScheduled(order.pickup_time || "");
    }
  }, [open, order]);

  const submit = async () => {
    if (!msg.trim()) return toast.error("Confirmation message is required");
    setSaving(true);
    try {
      await api.post(`/orders/${order.id}/confirm`, { confirmation_message: msg.trim(), scheduled_time: scheduled.trim() });
      toast.success(`${order.order_number} confirmed`);
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid={`order-confirm-${order.order_number}`}
          className="btn-brand rounded-full h-8 px-3 text-xs inline-flex items-center gap-1">
          <Check className="h-3 w-3" /> Confirm order
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#F5F5F0]" data-testid="confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl tracking-tight">Confirm {order.order_number}</DialogTitle>
          <DialogDescription className="text-[#4A4341]">
            The confirmation message below will be sent to the customer, and the order will move to Confirmed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-xs text-[#4A4341] bg-white border border-black/10 rounded p-3">
            <p><span className="label-eyebrow">Customer</span> · {order.customer_name} · {order.customer_phone}</p>
            <p className="mt-1"><span className="label-eyebrow">Type</span> · {order.fulfillment_type === "delivery" ? "Delivery" : "Pickup"} · {order.pickup_date}</p>
            {order.delivery_address ? <p className="mt-1"><span className="label-eyebrow">Address</span> · {order.delivery_address}</p> : null}
          </div>
          <div>
            <Label htmlFor="scheduled-time">Scheduled time (edit if needed)</Label>
            <Input id="scheduled-time" data-testid="confirm-time-input" value={scheduled} onChange={(e) => setScheduled(e.target.value)}
              className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]" />
          </div>
          <div>
            <Label htmlFor="confirm-msg">Confirmation message to customer</Label>
            <Textarea id="confirm-msg" data-testid="confirm-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} rows={4}
              className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]" />
            <p className="mt-1 text-[11px] text-[#7B7573]">Sent to the customer as an SMS if Twilio is configured — otherwise logged.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="confirm-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="confirm-submit-btn" className="btn-brand rounded-full px-6">
            {saving ? "Confirming…" : "Send confirmation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrderCard({ order, onAdvance, onCancel, onDelete, refresh }) {
  const next = NEXT_STATUS[order.status];
  const isDelivery = order.fulfillment_type === "delivery";
  return (
    <article data-testid={`order-card-${order.order_number}`}
      className="rounded-lg border border-black/10 bg-white p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-mono-jet text-xs text-[#C05A46]">{order.order_number}</p>
          <p className="mt-1 font-serif-display text-lg leading-tight">{order.customer_name}</p>
          <p className="text-xs text-[#7B7573] flex items-center gap-1 mt-0.5">
            <Phone className="h-3 w-3" strokeWidth={1.5} /> {order.customer_phone}
          </p>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full text-white"
          style={{ backgroundColor: STATUS_COLOR[order.status] || "#4A4341" }}>
          {STATUS_LABEL[order.status]}
        </span>
      </div>

      <div className="mt-3 text-xs flex items-center gap-2 text-[#4A4341]">
        {isDelivery ? <Truck className="h-3 w-3 text-[#C05A46]" /> : <Store className="h-3 w-3 text-[#7A8B76]" />}
        <span className="font-medium">{isDelivery ? "Delivery" : "Pickup"}</span>
        <span>·</span>
        <Clock className="h-3 w-3" /> {order.pickup_date} {order.pickup_time}
      </div>
      {isDelivery && (order.delivery_group || order.delivery_address) ? (
        <p className="mt-2 text-xs text-[#4A4341] bg-[#F5F5F0] rounded p-2 border border-black/5">
          {order.delivery_group || order.delivery_address}
          {order.delivery_group && order.delivery_address ? (
            <span className="block text-[10px] text-[#7B7573] mt-0.5">{order.delivery_address}</span>
          ) : null}
        </p>
      ) : null}

      <ul className="mt-3 space-y-1 text-xs text-[#1A1514]">
        {order.items.map((line, i) => (
          <li key={i} className="border-l-2 border-[#EAEAE3] pl-2">
            <p className="font-medium">{line.quantity}× {line.drink.name} <span className="text-[#7B7573]">{line.size}</span></p>
            <p className="text-[#7B7573]">
              {[line.milk?.name, ...line.syrups.map((s) => s.name), ...line.extras.map((e) => e.name)]
                .filter(Boolean).join(" · ") || "As-is"}
            </p>
            {line.notes ? <p className="italic text-[#7B7573]">&ldquo;{line.notes}&rdquo;</p> : null}
          </li>
        ))}
      </ul>

      {order.notes ? <p className="mt-3 text-xs italic text-[#4A4341] bg-[#EAEAE3]/60 rounded p-2">Note: {order.notes}</p> : null}

      <div className="mt-4 pt-3 border-t border-black/5 flex items-center justify-between gap-2">
        <span className="font-mono-jet text-sm">{currency(order.total)}</span>
        <div className="flex items-center gap-1 flex-wrap justify-end">
          {order.status === "pending" && (
            <>
              <Button size="sm" variant="ghost" data-testid={`order-cancel-${order.order_number}`}
                onClick={() => onCancel(order)} className="text-[#7B7573] hover:text-[#C05A46] h-8 px-2 text-xs">Cancel</Button>
              <ConfirmDialog order={order} refresh={refresh} />
            </>
          )}
          {order.status !== "pending" && order.status !== "cancelled" && order.status !== "completed" && (
            <>
              <Button size="sm" variant="ghost" data-testid={`order-cancel-${order.order_number}`}
                onClick={() => onCancel(order)} className="text-[#7B7573] hover:text-[#C05A46] h-8 px-2 text-xs">Cancel</Button>
              {next && (
                <Button size="sm" data-testid={`order-advance-${order.order_number}`}
                  onClick={() => onAdvance(order, next)}
                  className="btn-brand rounded-full h-8 px-3 text-xs inline-flex items-center gap-1">
                  → {STATUS_LABEL[next]} <ArrowRight className="h-3 w-3" />
                </Button>
              )}
            </>
          )}
          {(order.status === "completed" || order.status === "cancelled") && (
            <Button size="sm" variant="ghost" data-testid={`order-delete-${order.order_number}`}
              onClick={() => onDelete(order)} className="text-[#7B7573] hover:text-[#C05A46] h-8 px-2">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}

function OrdersBoard({ orders, refresh }) {
  const grouped = useMemo(() => {
    const g = { pending: [], confirmed: [], brewing: [], ready: [], completed: [] };
    orders.forEach((o) => { if (g[o.status]) g[o.status].push(o); });
    return g;
  }, [orders]);

  const advance = async (order, next) => {
    try {
      await api.patch(`/orders/${order.id}/status`, { status: next });
      toast.success(`${order.order_number} → ${STATUS_LABEL[next]}`);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const cancel = async (order) => {
    if (!confirm(`Cancel order ${order.order_number}?`)) return;
    try {
      await api.patch(`/orders/${order.id}/status`, { status: "cancelled" });
      toast.success(`${order.order_number} cancelled`);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (order) => {
    if (!confirm(`Delete order ${order.order_number}?`)) return;
    try { await api.delete(`/orders/${order.id}`); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="orders-board" className="grid grid-cols-1 md:grid-cols-5 gap-4 kanban-scroll">
      {STATUS_FLOW.map((status) => (
        <section key={status} data-testid={`orders-column-${status}`}>
          <header className="flex items-center justify-between mb-3 px-1">
            <div className="flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_COLOR[status] }} />
              <p className="label-eyebrow text-[#1A1514]">{STATUS_LABEL[status]}</p>
            </div>
            <span data-testid={`orders-count-${status}`} className="font-mono-jet text-xs text-[#7B7573]">{grouped[status].length}</span>
          </header>
          <div className="space-y-3 min-h-[100px]">
            {grouped[status].length === 0 ? (
              <p className="text-xs text-[#7B7573] italic px-1">No orders</p>
            ) : (
              grouped[status].map((o) => (
                <OrderCard key={o.id} order={o} onAdvance={advance} onCancel={cancel} onDelete={del} refresh={refresh} />
              ))
            )}
          </div>
        </section>
      ))}
    </div>
  );
}

function MenuItemEditor({ category, item, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(item?.name || "");
  const [description, setDescription] = useState(item?.description || "");
  const [price, setPrice] = useState(item?.price ?? 0);
  const [active, setActive] = useState(item?.active ?? true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(item?.name || ""); setDescription(item?.description || "");
      setPrice(item?.price ?? 0); setActive(item?.active ?? true);
    }
  }, [open, item]);

  const save = async () => {
    if (!name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = { name: name.trim(), description: description.trim(), price: Number(price) || 0, active };
      if (item) { await api.patch(`/menu/${category}/${item.id}`, payload); toast.success("Updated"); }
      else { await api.post(`/menu/${category}`, payload); toast.success("Added"); }
      setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#F5F5F0]" data-testid="menu-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl tracking-tight">
            {item ? "Edit item" : `Add ${category.slice(0, -1)}`}
          </DialogTitle>
          <DialogDescription className="text-[#4A4341]">
            {item ? "Update the name, description, price, or active state." : "Give it a name, an optional description, and a price."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label htmlFor="item-name">Name</Label>
            <Input id="item-name" data-testid="menu-item-name-input" value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]" />
          </div>
          <div><Label htmlFor="item-desc">Description</Label>
            <Textarea id="item-desc" data-testid="menu-item-desc-input" value={description} onChange={(e) => setDescription(e.target.value)}
              rows={2} className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label htmlFor="item-price">{
              category === "drinks" ? "Base price" :
              category === "sizes" ? "Size upcharge (0 for standard)" :
              "Add-on price"
            }</Label>
              <Input id="item-price" type="number" step="0.25" min="0" data-testid="menu-item-price-input"
                value={price} onChange={(e) => setPrice(e.target.value)}
                className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46] font-mono-jet" />
            </div>
            <div className="flex items-end gap-3">
              <div><Label>Active</Label>
                <div className="mt-2"><Switch checked={active} onCheckedChange={setActive} data-testid="menu-item-active-switch" /></div>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="menu-item-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="menu-item-save-btn" className="btn-brand rounded-full px-6">
            {saving ? "Saving…" : item ? "Save changes" : "Add item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MenuManager() {
  const [menu, setMenu] = useState({ drinks: [], sizes: [], milks: [], syrups: [], extras: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("drinks");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/menu"); setMenu(data); }
    catch { toast.error("Couldn't load the menu"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (category, item) => {
    if (!confirm(`Delete "${item.name}"?`)) return;
    try { await api.delete(`/menu/${category}/${item.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleActive = async (category, item) => {
    try { await api.patch(`/menu/${category}/${item.id}`, { active: !item.active }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="menu-manager">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#EAEAE3]">
          {CATEGORIES.map((c) => (
            <TabsTrigger key={c.key} value={c.key} data-testid={`menu-tab-${c.key}`}
              className="data-[state=active]:bg-white data-[state=active]:text-[#C05A46]">{c.label}</TabsTrigger>
          ))}
        </TabsList>
        {CATEGORIES.map((c) => (
          <TabsContent key={c.key} value={c.key} className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-serif-display text-2xl tracking-tight">{c.label}</h3>
                <p className="text-xs text-[#7B7573] mt-1">
                  {c.key === "drinks" ? "Base drinks customers can order."
                    : c.key === "sizes" ? "Size options with upcharges. Deactivate any you don't want customers to pick."
                    : `Optional ${c.label.toLowerCase()} for customization.`}
                </p>
              </div>
              <MenuItemEditor category={c.key} onSaved={load}
                trigger={<Button data-testid={`add-${c.key}-btn`} className="btn-brand rounded-full px-5 inline-flex items-center gap-2">
                  <Plus className="h-4 w-4" /> Add {c.label.slice(0, -1)}</Button>} />
            </div>
            <div className="border border-black/10 rounded-lg overflow-hidden bg-white">
              {loading ? <p className="p-6 text-sm text-[#7B7573]">Loading…</p>
                : menu[c.key].length === 0 ? (
                  <div className="p-10 text-center">
                    <p className="font-serif-display text-xl">No {c.label.toLowerCase()} yet.</p>
                    <p className="mt-1 text-sm text-[#7B7573]">Add your first {c.label.slice(0, -1).toLowerCase()} to get started.</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-black/5">
                    {menu[c.key].map((item) => (
                      <li key={item.id} data-testid={`menu-item-${item.id}`} className="flex items-center justify-between gap-4 p-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{item.name}</p>
                          {item.description ? <p className="text-xs text-[#7B7573] truncate">{item.description}</p> : null}
                        </div>
                        <span className="font-mono-jet text-sm text-[#4A4341]">{currency(item.price)}</span>
                        <div className="flex items-center gap-2">
                          <Switch checked={item.active} onCheckedChange={() => toggleActive(c.key, item)} data-testid={`menu-item-toggle-${item.id}`} />
                          <MenuItemEditor category={c.key} item={item} onSaved={load}
                            trigger={<Button variant="ghost" size="icon" data-testid={`menu-item-edit-${item.id}`}><Pencil className="h-4 w-4" /></Button>} />
                          <Button variant="ghost" size="icon" onClick={() => del(c.key, item)}
                            data-testid={`menu-item-delete-${item.id}`} className="text-[#7B7573] hover:text-[#C05A46]">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({ closed_for_day: false });
  const [tab, setTab] = useState("orders");

  const load = async () => {
    try {
      const [{ data: o }, { data: s }, { data: st }] = await Promise.all([
        api.get("/orders"),
        api.get("/orders/stats"),
        api.get("/settings"),
      ]);
      setOrders(o); setStats(s); setSettings(st);
    } catch (e) {
      // Auth handled by <Navigate> below; log for observability.
      console.debug("Admin load failed (likely unauthenticated).", e);
    }
  };
  useEffect(() => {
    if (user && user.role === "admin") { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }
  }, [user]);

  const toggleClosed = async () => {
    const next = !settings.closed_for_day;
    try {
      const { data } = await api.patch("/settings", { closed_for_day: next });
      setSettings(data);
      toast.success(next ? "Site closed — new orders blocked" : "Site reopened — accepting orders");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (user === null) return null;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-[#F5F5F0]">
      <header className="border-b border-black/10 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#C05A46] text-[#F5F5F0]">
              <Coffee className="h-4 w-4" strokeWidth={1.75} />
            </span>
            <div>
              <p className="font-serif-display text-lg leading-none">The Cozy Bean</p>
              <p className="text-[10px] tracking-widest uppercase text-[#7B7573]">Owner console · Dillsburg, PA</p>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 rounded-full border border-black/10 bg-[#F5F5F0] px-3 py-1.5">
              <PowerOff className={`h-3.5 w-3.5 ${settings.closed_for_day ? "text-[#C05A46]" : "text-[#7B7573]"}`} />
              <span className="text-xs text-[#4A4341]">{settings.closed_for_day ? "Closed today" : "Open"}</span>
              <Switch
                checked={settings.closed_for_day}
                onCheckedChange={toggleClosed}
                data-testid="closed-for-day-toggle"
              />
            </div>
            <span data-testid="admin-user-email" className="hidden md:inline text-xs text-[#4A4341]">{user.email}</span>
            <Button variant="ghost" onClick={logout} data-testid="admin-logout-btn" className="text-[#4A4341] hover:text-[#C05A46]">
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
        {settings.closed_for_day ? (
          <div data-testid="closed-banner" className="bg-[#C05A46] text-white text-xs py-1.5 text-center tracking-wide">
            You are marked CLOSED FOR THE DAY — customers can&rsquo;t place new orders.
          </div>
        ) : null}
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="rounded-lg border border-black/10 bg-white p-4">
            <p className="label-eyebrow text-[#7B7573]">Awaiting confirmation</p>
            <p data-testid="stat-pending" className="mt-1 font-serif-display text-3xl">{stats ? stats.pending : "—"}</p>
          </div>
          <div className="rounded-lg border border-black/10 bg-white p-4">
            <p className="label-eyebrow text-[#7B7573]">In progress</p>
            <p data-testid="stat-progress" className="mt-1 font-serif-display text-3xl">{stats ? stats.confirmed + stats.brewing + stats.ready : "—"}</p>
          </div>
          <div className="rounded-lg border border-black/10 bg-white p-4">
            <p className="label-eyebrow text-[#7B7573]">Orders today</p>
            <p data-testid="stat-orders-today" className="mt-1 font-serif-display text-3xl">{stats ? stats.orders_today : "—"}</p>
          </div>
          <div className="rounded-lg border border-black/10 bg-white p-4">
            <p className="label-eyebrow text-[#7B7573]">Revenue today</p>
            <p data-testid="stat-revenue-today" className="mt-1 font-serif-display text-3xl inline-flex items-baseline gap-1">
              <DollarSign className="h-5 w-5 text-[#7A8B76]" strokeWidth={1.75} />
              {stats ? Number(stats.revenue_today).toFixed(2) : "—"}
            </p>
          </div>
        </section>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#EAEAE3]">
            <TabsTrigger value="orders" data-testid="admin-tab-orders" className="data-[state=active]:bg-white data-[state=active]:text-[#C05A46]">Live orders</TabsTrigger>
            <TabsTrigger value="menu" data-testid="admin-tab-menu" className="data-[state=active]:bg-white data-[state=active]:text-[#C05A46]">Menu manager</TabsTrigger>
          </TabsList>
          <TabsContent value="orders" className="mt-6"><OrdersBoard orders={orders} refresh={load} /></TabsContent>
          <TabsContent value="menu" className="mt-6"><MenuManager /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
